import React, { useState, useRef, useCallback } from 'react';
import IntakeModal from './IntakeModal';

/**
 * Home-screen "drop files anywhere, get a filed project" entry point
 * (SMART_INTAKE_PLAN.md Phase 2). A slim always-visible card plus a
 * whole-page dragover overlay — dropping (or browsing) opens IntakeModal,
 * which owns the actual intake session end-to-end. Deliberately
 * self-contained so CustomerSelectionView only needs to render one line.
 */
const IntakeEntryPoint: React.FC = () => {
  const [dragActive, setDragActive] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[] | null>(null);
  const dragCounterRef = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const openWithFiles = (fileList: FileList | File[]) => {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setPendingFiles(files);
  };

  const handleWindowDragEnter = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current += 1;
    setDragActive(true);
  }, []);

  const handleWindowDragOver = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
  }, []);

  const handleWindowDragLeave = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  }, []);

  const handleWindowDrop = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setDragActive(false);
    if (e.dataTransfer.files.length > 0) openWithFiles(e.dataTransfer.files);
  }, []);

  React.useEffect(() => {
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);
    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, [handleWindowDragEnter, handleWindowDragOver, handleWindowDragLeave, handleWindowDrop]);

  return (
    <>
      <div
        onClick={() => fileInputRef.current?.click()}
        className="mb-4 flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-primary hover:text-primary dark:hover:text-primary cursor-pointer transition-colors"
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        <span>
          Drop policy documents here — Risk Ninja finds the customer and sets up the folder.{' '}
          <span className="text-primary underline">browse files</span>
        </span>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) openWithFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      {dragActive && !pendingFiles && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-primary/10 backdrop-blur-sm pointer-events-none">
          <div className="px-8 py-6 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border-2 border-dashed border-primary text-center">
            <p className="text-lg font-semibold text-secondary dark:text-dark-text">Drop to start intake</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Risk Ninja will find the customer and set up the folder.</p>
          </div>
        </div>
      )}

      {pendingFiles && (
        <IntakeModal files={pendingFiles} source="home" onClose={() => setPendingFiles(null)} />
      )}
    </>
  );
};

export default IntakeEntryPoint;
